import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Bell, X, Sparkles, ShieldAlert, Target, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/* Notification system — two surfaces:
 *
 *   <NotificationBell />     ← the popover with the full list (kept for
 *                              backward compat in old layouts/tests)
 *   <NotificationDock />     ← persistent top-right widget with bell + an
 *                              inline ticker that always shows the most
 *                              recent unread without needing a click.
 *
 * Both consume a tiny shared hook so we only hit /notifications once per
 * mount cycle.
 */

const TYPE_ICON = {
    mission_completed: Sparkles,
    signals_ready: Target,
    approval_pending: ShieldAlert,
    verification_warning: ShieldAlert,
    system: Bell,
};

function useNotifications() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/notifications?limit=20");
            setItems(r.data.items || []);
            setUnread(r.data.unread || 0);
        } catch { /* silent */ }
    }, []);
    useEffect(() => {
        refresh();
        let intervalId = null;
        const start = () => { if (!intervalId) intervalId = setInterval(refresh, 30000); };
        const stop = () => { if (intervalId) { clearInterval(intervalId); intervalId = null; } };
        if (!document.hidden) start();
        const onVis = () => { if (document.hidden) stop(); else { refresh(); start(); } };
        document.addEventListener("visibilitychange", onVis);
        return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    }, [refresh]);
    return { items, unread, refresh, setItems, setUnread };
}


/* -------------------------------------------------------------------------- */
/*  Shared dropdown body                                                      */
/* -------------------------------------------------------------------------- */
function DropdownList({ items, unread, refresh, onNavigate }) {
    const { t } = useTranslation();
    const markRead = async (id) => {
        try { await api.post(`/notifications/${id}/read`); refresh(); } catch { /* silent */ }
    };
    const markAll = async () => {
        try { await api.post(`/notifications/read-all`); toast.success(t("notif.all_read")); refresh(); } catch { /* silent */ }
    };
    const open_link = (n) => {
        if (!n.read) markRead(n.id);
        if (n.link) onNavigate(n.link);
    };
    return (
        <>
            <div className="sticky top-0 bg-[var(--jr-surface)]/95 backdrop-blur px-3 py-2.5 border-b border-white/8 flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                    {t("notif.title")} {unread > 0 && <span className="text-[var(--jr-brand)]">· {unread} {t("notif.new")}</span>}
                </span>
                {items.length > 0 && (
                    <button onClick={markAll} data-testid="notif-mark-all-read"
                        className="text-[10px] text-[var(--jr-text-2)] hover:text-white inline-flex items-center gap-1">
                        <CheckCheck size={11} /> {t("notif.mark_all")}
                    </button>
                )}
            </div>
            {items.length === 0 ? (
                <div className="p-6 text-center text-xs text-[var(--jr-text-3)]">
                    {t("notif.empty")}
                </div>
            ) : (
                <ul className="divide-y divide-white/5">
                    {items.map((n) => {
                        const Icon = TYPE_ICON[n.type] || Bell;
                        return (
                            <li key={n.id} data-testid={`notif-${n.id}`}
                                className={`px-3 py-3 cursor-pointer hover:bg-white/[0.04] ${!n.read ? "bg-[var(--jr-brand)]/[0.03]" : ""}`}
                                onClick={() => open_link(n)}>
                                <div className="flex items-start gap-2">
                                    <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${!n.read ? "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)]" : "bg-white/5 text-[var(--jr-text-3)]"}`}>
                                        <Icon size={13} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <h4 className={`text-xs font-medium truncate ${!n.read ? "text-white" : "text-[var(--jr-text-2)]"}`}>{n.title}</h4>
                                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[var(--jr-brand)] shrink-0 mt-1.5" />}
                                        </div>
                                        {n.message && <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5 line-clamp-2 leading-snug">{n.message}</p>}
                                        <p className="text-[10px] font-mono text-[var(--jr-text-3)] mt-1">
                                            {(n.created_at || "").replace("T", " ").slice(0, 16)}
                                        </p>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); api.delete(`/notifications/${n.id}`).then(refresh); }}
                                        data-testid={`notif-del-${n.id}`}
                                        className="p-1 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:bg-white/5"
                                        aria-label={t("notif.delete")}>
                                        <X size={11} />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </>
    );
}


/* -------------------------------------------------------------------------- */
/*  Legacy popover bell — kept exported so existing imports & tests pass.     */
/* -------------------------------------------------------------------------- */
export default function NotificationBell({ placement = "up" }) {
    const [open, setOpen] = useState(false);
    const popRef = useRef(null);
    const nav = useNavigate();
    const { items, unread, refresh } = useNotifications();

    useEffect(() => {
        const handler = (e) => {
            if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const dropdownPos = placement === "down" ? "top-full mt-2" : "bottom-full mb-2";

    return (
        <div className="relative" ref={popRef}>
            <button onClick={() => setOpen(!open)} data-testid="notification-bell"
                className="relative p-2 rounded-lg text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                <Bell size={16} />
                {unread > 0 && (
                    <span data-testid="notification-unread-count"
                        className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[var(--jr-error)] text-white text-[9px] font-mono font-bold grid place-items-center">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
            {open && (
                <div data-testid="notification-panel"
                    className={`absolute right-0 ${dropdownPos} w-80 max-h-[480px] overflow-y-auto rounded-xl border border-white/10 bg-[var(--jr-surface)] shadow-[0_8px_40px_rgba(0,0,0,0.4)] z-50`}>
                    <DropdownList items={items} unread={unread} refresh={refresh}
                        onNavigate={(link) => { setOpen(false); nav(link); }} />
                </div>
            )}
        </div>
    );
}


/* -------------------------------------------------------------------------- */
/*  Persistent top-right dock — always visible, with inline ticker            */
/* -------------------------------------------------------------------------- */
export function NotificationDock({ inline = false }) {
    const [open, setOpen] = useState(false);
    const popRef = useRef(null);
    const nav = useNavigate();
    const { items, unread, refresh } = useNotifications();

    useEffect(() => {
        const handler = (e) => {
            if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // The very first unread item drives... [ticker DISABLED per user request —
    // the user found the persistent inline pill next to the bell noisy and
    // interfering with the interface. Clicking the bell is enough.]

    return (
        <div ref={popRef}
            className={inline
                ? "relative flex items-center gap-2"
                : "fixed top-3 right-4 z-40 flex items-center gap-2"}
            data-testid="notification-dock">

            {/* Bell button */}
            <button onClick={() => setOpen(!open)} data-testid="notification-dock-bell"
                className="relative p-2 rounded-lg bg-[var(--jr-surface)]/85 backdrop-blur border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-[var(--jr-surface)] transition-colors">
                <Bell size={16} />
                {unread > 0 && (
                    <span data-testid="dock-unread-count"
                        className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[var(--jr-error)] text-white text-[9px] font-mono font-bold grid place-items-center">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {/* Popover dropdown — opens DOWN since we're at the top */}
            {open && (
                <div data-testid="notification-panel"
                    className="absolute right-0 top-full mt-2 w-96 max-h-[520px] overflow-y-auto rounded-xl border border-white/10 bg-[var(--jr-surface)] shadow-[0_12px_50px_rgba(0,0,0,0.45)]">
                    <DropdownList items={items} unread={unread} refresh={refresh}
                        onNavigate={(link) => { setOpen(false); nav(link); }} />
                </div>
            )}
        </div>
    );
}
